import * as React from "react";
import { find, get, map, trim } from "lodash";
import classnames from "classnames";
import { Box, Flex } from "@rebass/grid";
import {
  Button,
  Classes,
  Dialog,
  FormGroup,
  InputGroup,
  Intent,
  HTMLTable,
  Text,
  Icon,
  Position,
  Checkbox,
  Alignment,
} from "@blueprintjs/core";
import { Select2 } from "@blueprintjs/select";
import { MenuItem2 } from "@blueprintjs/popover2";

import DimensionRow from "./DimensionRow";

import {
  Dimension,
  OutputDimensionMapping,
  Output,
  OutputTag,
} from "../../../types/models";
import { Popover2, Tooltip2 } from "@blueprintjs/popover2";
import SuppressReportingDimensionRow from "./SuppressReportingDimensionRow";
import { ClientMap } from "src/store/modules/client";

interface IProps {
  isOpen: boolean;
  output?: Output;
  modelInstanceId: number;
  dimensions: Dimension[];
  isValid(output: Output): boolean;
  onSave(output: Output): void;
  onCancel(): void;
  onDelete(output: Output): void;
  outputTags: OutputTag[];
  // Every Output already defined on this model instance, so the Output Tag
  // selector below can exclude tags already claimed by a DIFFERENT output -
  // each Output Tag maps to at most one Output per instance (CLD-3013).
  // Inputs have their own, separate Output Tag selector that this
  // restriction does not apply to.
  allOutputs: Output[];
  clients: ClientMap;
  clientId: number;
}

interface IState {
  errorMsg: string;
  editedOutput: Output;
  areDimensionsColumnsVisible: boolean;
}

const tableHeaderClasses = classnames(Classes.TEXT_MUTED);
const errorMsgClasses = classnames(Classes.TEXT_SMALL, Classes.INTENT_DANGER);

// Output Tag is optional - a user may want to un-assign a tag they'd
// previously set. An empty-string value (rather than omitting it) is what
// lets the save path's existing `output?.OutputTag?.value || null` fall
// through to "no tag" (see OutputEditorView.handleEditSave), matching the
// Input side's equivalent "(None)" selector.
const NONE_OUTPUT_TAG_OPTION = { label: "(None)", value: "" };

const customDialogCSS = {
  minWidth: "600px",
};

class EditOutputModal extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props);

    const { output, dimensions, modelInstanceId } = this.props;
    if (output) {
      const selectedOutputTag = this.props.outputTags.find(
        (tag) => tag.id === output?.TagID
      );
      if (selectedOutputTag) {
        output["OutputTag"] = {
          label: selectedOutputTag?.Name,
          value: selectedOutputTag?.id,
        };
      }
    }

    this.state = {
      errorMsg: "",
      editedOutput: output || this.newOutput(modelInstanceId, dimensions),
      areDimensionsColumnsVisible: false,
    };
    this.handleDimensionsColumnVisibility =
      this.handleDimensionsColumnVisibility.bind(this);
  }

  public render() {
    const { isOpen, output, dimensions, outputTags, allOutputs } = this.props;
    const { editedOutput, errorMsg, areDimensionsColumnsVisible } = this.state;

    // A tag "used" by this same output (i.e. its own current assignment)
    // must stay selectable - only exclude tags claimed by OTHER outputs.
    const tagIdsUsedByOtherOutputs = new Set(
      (allOutputs || [])
        .filter((o) => o.id !== editedOutput.id && o.TagID != null)
        .map((o) => o.TagID)
    );
    const availableOutputTags = outputTags.filter(
      (tag) => !tagIdsUsedByOtherOutputs.has(tag.id)
    );

    return (
      <Dialog
        isOpen={isOpen}
        title={output ? "Edit Output" : "New Output"}
        onClose={this.handleCancel}
        style={customDialogCSS}
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup
            label="Output Name"
            labelFor="output-name-input"
            labelInfo="(required)"
          >
            <InputGroup
              id="output-name-input"
              defaultValue={editedOutput.Name}
              onChange={this.handleNameChange}
              placeholder="Output Name"
              required={true}
            />
            {errorMsg && (
              <Box py={1}>
                <Text className={errorMsgClasses}>{errorMsg}</Text>
              </Box>
            )}
          </FormGroup>
          {this.props.clients[this.props.clientId].IsClientTaxonomyEnabled && (
            <FormGroup label="Output Tag Name" labelFor="output-tags-select">
              <Select2<{ label: string; value: string }>
                items={[
                  NONE_OUTPUT_TAG_OPTION,
                  ...availableOutputTags.map((tag) => ({
                    label: tag.Name,
                    value: String(tag.id),
                  })),
                ]}
                noResults={<MenuItem2 disabled={true} text="No results." />}
                itemRenderer={(item, { modifiers, handleClick }) => (
                  <MenuItem2
                    active={modifiers.active}
                    key={item.value}
                    onClick={handleClick}
                    text={item.label || "(None)"}
                  />
                )}
                onItemSelect={(item) =>
                  this.setState({
                    editedOutput: {
                      ...editedOutput,
                      OutputTag: item.value ? item : null,
                    },
                  })
                }
                filterable={true}
                resetOnClose={true}
                popoverProps={{ matchTargetWidth: true }}
              >
                <Button
                  fill={true}
                  alignText={Alignment.LEFT}
                  rightIcon="caret-down"
                  text={editedOutput.OutputTag?.label || "Select output tag"}
                />
              </Select2>
            </FormGroup>
          )}
          <Checkbox
            checked={editedOutput.DisableOutputAggregation}
            label="Disable aggregation in Forecast Manager"
            onChange={this.handleChangeDisableAggregation}
          />
          <Flex>
            <Box width="95%">
              <HTMLTable className="edit-output-dimensions-table">
                <thead>
                  <tr>
                    <th>
                      <Box>
                        <Text className={tableHeaderClasses}>Dimension</Text>
                      </Box>
                    </th>
                    <th>
                      <Box>
                        <Text className={tableHeaderClasses}>Report</Text>
                      </Box>
                    </th>
                    <th>
                      <Box>
                        <Text className={tableHeaderClasses}>Aggregate</Text>
                      </Box>
                    </th>
                    {areDimensionsColumnsVisible && (
                      <>
                        <th>
                          <Box>
                            <Text className={tableHeaderClasses}>
                              Dimension Total 1
                            </Text>
                          </Box>
                        </th>
                        <th>
                          <Box>
                            <Text className={tableHeaderClasses}>
                              Dimension Total 2
                            </Text>
                          </Box>
                        </th>
                        <th>
                          <Box>
                            <Text className={tableHeaderClasses}>
                              Dimension Total 3
                            </Text>
                          </Box>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {map(dimensions, (dimension, idx) => {
                    const dimensionMapping = find(
                      editedOutput.dimensionMapping,
                      (mapping) => mapping.DimensionID === dimension.id
                    );
                    return (
                      dimensionMapping && (
                        <DimensionRow
                          key={`${dimension.id}-${idx}`}
                          dimension={dimension}
                          dimensionMapping={dimensionMapping}
                          onChange={this.handleDimensionMappingChange}
                          isLowestDimension={idx === dimensions.length - 1}
                          areDimensionsColumnsVisible={
                            areDimensionsColumnsVisible
                          }
                        />
                      )
                    );
                  })}
                  {areDimensionsColumnsVisible && (
                    <SuppressReportingDimensionRow
                      suppressReportingDimensionTotal1={
                        this.state.editedOutput.SuppressReportingDimensionTotal1
                      }
                      suppressReportingDimensionTotal2={
                        this.state.editedOutput.SuppressReportingDimensionTotal2
                      }
                      suppressReportingDimensionTotal3={
                        this.state.editedOutput.SuppressReportingDimensionTotal3
                      }
                      handleSuppressReportingDimensionMappingChange={
                        this.handleSuppressReportingDimensionMappingChange
                      }
                    />
                  )}
                </tbody>
              </HTMLTable>
            </Box>
            <Box width="5%" alignSelf="center">
              <Popover2 position={Position.RIGHT}>
                <Tooltip2
                  content="Dimension Total Aggregation"
                  position={Position.RIGHT}
                >
                  <Button onClick={this.handleDimensionsColumnVisibility}>
                    <Icon
                      icon={
                        areDimensionsColumnsVisible
                          ? "double-chevron-left"
                          : "double-chevron-right"
                      }
                    />
                  </Button>
                </Tooltip2>
              </Popover2>
            </Box>
          </Flex>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <Flex justifyContent="space-between">
            <Box>
              {output && (
                <Button intent={Intent.DANGER} onClick={this.handleDelete}>
                  Delete
                </Button>
              )}
            </Box>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.handleCancel}>Cancel</Button>
              <Button intent={Intent.PRIMARY} onClick={this.handleSave}>
                {output ? "Update Output" : "Create Output"}
              </Button>
            </div>
          </Flex>
        </div>
      </Dialog>
    );
  }

  private handleDimensionsColumnVisibility() {
    this.setState({
      areDimensionsColumnsVisible: !this.state.areDimensionsColumnsVisible,
    });
  }

  private handleNameChange = (e: React.FormEvent<HTMLInputElement>): void =>
    this.setState({
      editedOutput: {
        ...this.state.editedOutput,
        Name: trim(e.currentTarget.value),
      },
    });

  private handleChangeDisableAggregation = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    this.setState({
      editedOutput: {
        ...this.state.editedOutput,
        DisableOutputAggregation: e.currentTarget.checked,
      },
    });
  };

  private handleDimensionMappingChange = (
    dimensionMapping: OutputDimensionMapping
  ): void => {
    this.setState({
      editedOutput: {
        ...this.state.editedOutput,
        dimensionMapping: this.state.editedOutput.dimensionMapping
          .filter(
            (mapping: OutputDimensionMapping) =>
              mapping.DimensionID !== dimensionMapping.DimensionID
          )
          .concat(dimensionMapping),
      },
    });
  };

  private handleSuppressReportingDimensionMappingChange = (
    SuppressReportingDimensionTotal1: boolean,
    SuppressReportingDimensionTotal2: boolean,
    SuppressReportingDimensionTotal3: boolean
  ): void => {
    this.setState({
      editedOutput: {
        ...this.state.editedOutput,
        SuppressReportingDimensionTotal1,
        SuppressReportingDimensionTotal2,
        SuppressReportingDimensionTotal3,
      },
    });
  };

  private handleCancel = (): void => this.props.onCancel();

  private handleSave = (): void => {
    const { isValid, onSave } = this.props;
    const { editedOutput } = this.state;

    if (isValid(editedOutput)) {
      onSave(editedOutput);
    } else {
      this.setState({
        errorMsg: "Please provide a unique Name",
      });
    }
  };

  private handleDelete = (): void =>
    this.props.onDelete(this.props.output as Output);

  private newOutput = (
    modelInstanceId: number,
    dimensions: Dimension[]
  ): Output => ({
    ModelInstanceID: modelInstanceId,
    Name: "",
    dimensionMapping: this.newDimensionMappings(dimensions),
    Dimension_Aggregation_FunctionID: null,
    Timescale_Aggregation_FunctionID: null,
    SuppressReportingDimensionTotal1: false,
    SuppressReportingDimensionTotal2: false,
    SuppressReportingDimensionTotal3: false,
    DisableOutputAggregation: false,
    OutputTag: null,
  });

  private newDimensionMappings = (
    dimensions: Dimension[]
  ): OutputDimensionMapping[] =>
    map(dimensions, (dimension: Dimension) => ({
      DimensionID: get(dimension, "id", 0) as number,
      Reporting_Flag: 0,
      Aggregation_Flag: 0,
      Dimension_Total_1: 0,
      Dimension_Total_2: 0,
      Dimension_Total_3: 0,
    }));
}

export default EditOutputModal;
